import {
    BadgeCheck,
    Bell,
    ChevronsUpDown,
    LogOut,
    Moon,
    Sun,
} from 'lucide-react'
import { useContext, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTheme } from 'next-themes'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    useSidebar,
} from '@/components/ui/sidebar'
import { cn } from '@/lib/utils'
import { isProd } from '@/utils/common'
import { AuthContext } from '@/contexts/AuthContext'

export function NavUser() {
    const { isMobile, state } = useSidebar()
    const { kalviumKc } = useContext(AuthContext)
    const { theme, setTheme } = useTheme()
    const [mounted, setMounted] = useState(false)
    const isCollapsed = state === 'collapsed'

    useEffect(() => {
        setMounted(true)
    }, [])

    // Get user data from token
    const parsedToken = kalviumKc?.getParsedToken()
    const user = {
        name: parsedToken?.name || parsedToken?.email?.split('@')[0] || 'User',
        email: parsedToken?.email || '',
        avatar: '', // You can add avatar URL from profile API if available
    }

    // First letter of first name and last name
    const avatarFallbackText =
        user?.name
            ?.split(' ')
            .map(n => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2) || 'U'

    const handleLogout = async () => {
        if (kalviumKc) {
            kalviumKc.doLogout()
        }
    }

    const handleThemeToggle = () => {
        if (isProd()) return // Prevent theme toggle in production
        setTheme(theme === 'dark' ? 'light' : 'dark')
    }

    const showThemeToggle = !isProd() && mounted

    if (!kalviumKc?.isLoggedIn()) {
        return null
    }

    return (
        <SidebarMenu className="w-full">
            <SidebarMenuItem className="w-full border-0">
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <SidebarMenuButton
                            size="lg"
                            className={cn(
                                'w-full data-[state=open]:bg-gray-100 dark:data-[state=open]:bg-gray-800 data-[state=open]:text-gray-900 dark:data-[state=open]:text-gray-100 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-900 dark:text-gray-100 transition-colors border-0 outline-none focus:outline-none focus:ring-0',
                                isCollapsed && 'justify-center'
                            )}
                        >
                            <Avatar className="h-8 w-8 rounded-lg">
                                <AvatarImage
                                    src={user.avatar}
                                    alt={user.name}
                                />
                                <AvatarFallback className="rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                                    {avatarFallbackText}
                                </AvatarFallback>
                            </Avatar>
                            <div
                                className={cn(
                                    'grid flex-1 text-left text-sm leading-tight',
                                    isCollapsed && 'hidden'
                                )}
                            >
                                <span className="truncate font-semibold text-gray-900 dark:text-gray-100">
                                    {user.name}
                                </span>
                                <span className="truncate text-xs text-gray-600 dark:text-gray-400">
                                    {user.email}
                                </span>
                            </div>
                            <ChevronsUpDown
                                className={cn(
                                    'ml-auto size-4 text-gray-600 dark:text-gray-400',
                                    isCollapsed && 'hidden'
                                )}
                            />
                        </SidebarMenuButton>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        className="min-w-56 rounded-lg"
                        side={isMobile ? 'bottom' : 'right'}
                        align="end"
                        sideOffset={4}
                    >
                        <DropdownMenuLabel className="p-0 font-normal">
                            <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                                <Avatar className="h-8 w-8 rounded-lg">
                                    <AvatarImage
                                        src={user.avatar}
                                        alt={user.name}
                                    />
                                    <AvatarFallback className="rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                                        {avatarFallbackText}
                                    </AvatarFallback>
                                </Avatar>
                                <div className="grid flex-1 text-left text-sm leading-tight">
                                    <span className="truncate font-semibold text-popover-foreground dark:text-gray-100">
                                        {user.name}
                                    </span>
                                    <span className="truncate text-xs text-muted-foreground dark:text-gray-400">
                                        {user.email}
                                    </span>
                                </div>
                            </div>
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuSeparator />
                        <DropdownMenuGroup>
                            <Link to="/account">
                                <DropdownMenuItem>
                                    <BadgeCheck />
                                    <span className="flex items-center gap-2">
                                        Account
                                        <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-semibold rounded-full border border-blue-200 dark:border-blue-800">
                                            BETA
                                        </span>
                                    </span>
                                </DropdownMenuItem>
                            </Link>
                            {/* <DropdownMenuItem>
                                <Bell />
                                Notifications
                            </DropdownMenuItem> */}
                        </DropdownMenuGroup>
                        {showThemeToggle && (
                            <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={handleThemeToggle}>
                                    {theme === 'dark' ? (
                                        <>
                                            <Sun className="h-4 w-4" />
                                            Light Mode
                                        </>
                                    ) : (
                                        <>
                                            <Moon className="h-4 w-4" />
                                            <span className="flex items-center gap-2">
                                                Dark Mode
                                                <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-semibold rounded-full border border-blue-200 dark:border-blue-800">
                                                    BETA
                                                </span>
                                            </span>
                                        </>
                                    )}
                                </DropdownMenuItem>
                            </>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={handleLogout}>
                            <LogOut />
                            Log out
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </SidebarMenuItem>
        </SidebarMenu>
    )
}
